import { HttpClient, HttpEventType, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, filter, map } from 'rxjs';
import {
  FileMetadata,
  SapUploadParameters,
  SoffApiListResponse,
  SoffApiSingleResponse,
  UploadConfig,
  UploadEvent,
  UploadResponse,
  Visibility,
} from './attachment.model';
import { environment } from '../../environments/environment';

/** Fallback-Content-Type, falls der Browser für eine Datei keinen MIME-Typ ermitteln kann. */
const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

/**
 * Reicht Datei-Uploads an `soff-sap-svc-fileupload-api-v1` weiter und bietet
 * Methoden für alle GET-Endpunkte (Auflisten, Download, Thumbnail).
 *
 * Upload-Endpunkt: `POST /{aggregateType}/{aggregateId}` mit dem rohen Binary-Body
 * der Datei (genau eine Datei pro Request); `visibility`, `position` und `fileName`
 * kommen als Query-Parameter, `Content-Type` wird aus der Datei übernommen. Die
 * Antwort ist ein `SoffApiResponseSingleDto<UploadResponse>`. Bei mehreren Dateien
 * ruft der Aufrufer {@link upload} einmal pro Datei auf.
 */
@Injectable({ providedIn: 'root' })
export class SapFileUploadService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/integration/sap-upload/v1`;
  private readonly configUrl = `${this.baseUrl}/config`;

  /**
   * Lädt die zentrale Upload-Konfiguration (Limits und Datei-Typ-Policy), damit
   * der Client vor dem Upload validieren kann.
   *
   * @param jwt Optionales Bearer-Token; wird als `Authorization`-Header gesetzt.
   */
  public getConfig(jwt?: string): Observable<UploadConfig> {
    return this.http.get<UploadConfig>(this.configUrl, { headers: this.authHeaders(jwt) });
  }

  /**
   * Lädt eine einzelne Datei als rohen Binary-Body hoch.
   *
   * `aggregateType` und `aggregateId` werden als Pfad-Variablen übertragen;
   * `visibility`, `position`, `fileName`, `category` und `tags` als
   * Query-Parameter. `Content-Type` wird aus dem MIME-Typ der Datei gesetzt
   * (Fallback `application/octet-stream`); `Content-Length` ermittelt der
   * Browser selbst aus dem Blob-Body.
   *
   * @param file Die hochzuladende Datei.
   * @param parameters Die SAP-Pflicht-Parameter.
   * @param jwt Optionales Bearer-Token.
   * @returns Strom aus Fortschritts-Ereignissen, abgeschlossen durch ein
   *   `response`-Ereignis mit dem Ergebnis dieser Datei.
   */
  public upload(
    file: File,
    parameters: SapUploadParameters,
    jwt?: string,
  ): Observable<UploadEvent> {
    let params = new HttpParams({
      fromObject: {
        visibility: parameters.visibility,
        position: parameters.position,
        fileName: file.name,
      },
    });

    const uploadUser = parameters.uploadUser?.trim();
    if (uploadUser) {
      params = params.set('uploadUser', uploadUser);
    }

    const category = parameters.category?.trim();
    if (category) {
      params = params.set('category', category);
    }

    const tags = (parameters.tags ?? []).map((tag) => tag.trim()).filter((tag) => tag.length > 0);
    if (tags.length > 0) {
      params = params.set('tags', tags.join(','));
    }

    const url = `${this.baseUrl}/${parameters.aggregateType}/${parameters.aggregateId}`;
    const headers = this.authHeaders(jwt).set('Content-Type', file.type || DEFAULT_CONTENT_TYPE);

    return this.http
      .post<SoffApiSingleResponse<UploadResponse>>(url, file, {
        params,
        headers,
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event): UploadEvent | null => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
            return { type: 'progress', progress };
          }
          if (event.type === HttpEventType.Response) {
            return event.body ? { type: 'response', response: event.body.data } : null;
          }
          return null;
        }),
        filter((event): event is UploadEvent => event !== null),
      );
  }

  /**
   * Listet alle Datei-Anhänge für ein Aggregat auf.
   *
   * @param aggregateType SOFF-Aggregat-Typ.
   * @param aggregateId SOFF-Aggregat-ID.
   * @param visibilities Optionaler Sichtbarkeits-Filter (kommasepariert).
   * @param category Optionaler Kategorie-Filter (`SapCategory:<value>`).
   * @param limit Max. Anzahl Ergebnisse (Default serverseitig: 100).
   * @param offset Offset für Paginierung (Default: 0).
   * @param jwt Optionales Bearer-Token.
   */
  public listFiles(
    aggregateType: string,
    aggregateId: string,
    visibilities?: Visibility[],
    category?: string,
    limit?: number,
    offset?: number,
    jwt?: string,
  ): Observable<FileMetadata[]> {
    let params = new HttpParams();
    if (visibilities && visibilities.length > 0) {
      params = params.set('visibility', visibilities.join(','));
    }
    if (category?.trim()) {
      params = params.set('category', category.trim());
    }
    if (limit !== undefined) {
      params = params.set('limit', String(limit));
    }
    if (offset !== undefined) {
      params = params.set('offset', String(offset));
    }
    const url = `${this.baseUrl}/${aggregateType}/${aggregateId}`;
    return this.http
      .get<SoffApiListResponse<FileMetadata>>(url, { headers: this.authHeaders(jwt), params })
      .pipe(map((response) => response.data));
  }

  /**
   * Lädt mehrere Dateien als ZIP-Archiv herunter.
   *
   * @param aggregateType SOFF-Aggregat-Typ.
   * @param aggregateId SOFF-Aggregat-ID.
   * @param ids Optionale Liste von Datei-UUIDs; leer = alle Dateien des Aggregats.
   * @param jwt Optionales Bearer-Token.
   */
  public downloadArchive(
    aggregateType: string,
    aggregateId: string,
    ids?: string[],
    jwt?: string,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (ids && ids.length > 0) {
      params = params.set('ids', ids.join(','));
    }
    const url = `${this.baseUrl}/${aggregateType}/${aggregateId}/archive`;
    return this.http.get(url, { headers: this.authHeaders(jwt), params, responseType: 'blob' });
  }

  /**
   * Streamt den Inhalt einer Datei als Blob.
   *
   * @param aggregateType SOFF-Aggregat-Typ.
   * @param aggregateId SOFF-Aggregat-ID.
   * @param fileId ID der Datei.
   * @param action `'download'` setzt `Content-Disposition: attachment`; ohne
   *   Parameter wird die Datei inline geliefert.
   * @param jwt Optionales Bearer-Token.
   */
  public downloadFile(
    aggregateType: string,
    aggregateId: string,
    fileId: string,
    action?: 'download',
    jwt?: string,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (action) {
      params = params.set('action', action);
    }
    const url = `${this.baseUrl}/${aggregateType}/${aggregateId}/${fileId}/download`;
    return this.http.get(url, { headers: this.authHeaders(jwt), params, responseType: 'blob' });
  }

  /**
   * Lädt das PNG-Thumbnail einer Datei als Blob.
   *
   * @param aggregateType SOFF-Aggregat-Typ.
   * @param aggregateId SOFF-Aggregat-ID.
   * @param fileId ID der Datei.
   * @param jwt Optionales Bearer-Token.
   */
  public thumbnailFile(
    aggregateType: string,
    aggregateId: string,
    fileId: string,
    jwt?: string,
  ): Observable<Blob> {
    const url = `${this.baseUrl}/${aggregateType}/${aggregateId}/${fileId}/thumbnail`;
    return this.http.get(url, { headers: this.authHeaders(jwt), responseType: 'blob' });
  }

  private authHeaders(jwt?: string): HttpHeaders {
    let headers = new HttpHeaders();
    if (jwt) {
      headers = headers.set('Authorization', `Bearer ${jwt}`);
    }
    return headers;
  }
}
